import type { OpsManagerState } from "../../types/contract";

// Pure + tested: both rules below have exactly ONE definition, which is the point — compute
// explicitly asked that drift not be defined twice.

// The distinction that must survive to the screen (compute, verbatim in spirit): on a failed GCE
// call they LEAVE THE PREVIOUS VALUE STANDING rather than writing 0 — so `live_vms: 0` means "I
// looked and there were none", while a stale `as_of` means "I couldn't look".
export const VM_STALE_AFTER_MS = 15 * 60 * 1000; // 3 missed 5-min reconciles

export interface VmObservation {
  count: number | null;
  asOf: string | null;
  /* * true = the number is old enough that it reports a failed look, not a live count */
  stale: boolean;
  /* * nothing has ever been stamped (pre-deploy, or the grant isn't in place) */
  unknown: boolean;
}

export function vmObservation(
  m: OpsManagerState | undefined,
  nowMs: number,
): VmObservation {
  // TOP-LEVEL on BE-Light's response (an earlier draft read `capacity.observed`, which was compute's
  // storage location, not the wire shape). `0` is a real zero; `null` is "compute hasn't reported".
  const count = typeof m?.live_vms === "number" ? m.live_vms : null;
  const asOf = m?.live_vms_as_of ?? null;
  if (count == null) return { count: null, asOf, stale: false, unknown: true };
  const t = asOf ? Date.parse(asOf) : NaN;
  const stale = Number.isFinite(t) ? nowMs - t > VM_STALE_AFTER_MS : true;
  return { count, asOf, stale, unknown: false };
}

/* * "1 VM · as of 01:49" / "1 VM · last seen 01:49 (couldn't reach GCE since)" / "—". */
export function vmLabel(v: VmObservation): string {
  if (v.unknown) return "VMs —";
  const hhmm = v.asOf ? v.asOf.slice(11, 16) : "?";
  const n = `${v.count} VM${v.count === 1 ? "" : "s"}`;
  return v.stale ? `${n} · last seen ${hhmm}` : `${n} · as of ${hhmm}`;
}

// ---- Process drift: DELIBERATELY NOT COMPUTED HERE --------------------------
// BE-Light computes `drift` + `drift_ratio` SERVER-SIDE (>=5 samples, ratio outside 0.67-1.5x) so the
// dashboard and any future alerting share ONE definition -- which is exactly what compute asked for when
// they pointed out drift isn't a column. An earlier draft of this file recomputed it client-side with a
// 1.25x factor; that was a second definition of the same concept and is gone. The FE RENDERS their
// verdict and their ratio. If the rule ever needs changing, it changes in their service, once.

// Deliberately DATA-DRIVEN: it walks whatever keys exist rather than hard-coding
// workers/cpu/ram/gee/jd, so a new scarce token (a new external API, another VM class) appears here
// the day compute adds it instead of being silently missing until someone notices.
export interface CapacityRow {
  key: string;
  label: string;
  used: number;
  total: number;
  /* * true for the scarce-token bag — the ones that gate external APIs */
  scarce: boolean;
}

const LABELS: Record<string, string> = {
  workers: "Workers",
  cpu: "CPU",
  ram_gb: "RAM (GB)",
  vm_workers: "VM workers",
  gee: "Earth Engine jobs",
  jd: "John Deere jobs",
};
const pretty = (k: string) =>
  LABELS[k] ?? k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

function compareCapacity(a: CapacityRow, b: CapacityRow): number {
  // scarce tokens first — they're the ones that actually gate throughput
  return Number(b.scarce) - Number(a.scarce) || a.label.localeCompare(b.label);
}

function pushBudget(
  out: CapacityRow[],
  key: string,
  value: unknown,
  reserved: Record<string, unknown>,
): void {
  const total = num(value);
  if (total != null) {
    out.push({
      key,
      label: pretty(key),
      used: num(reserved[key]) ?? 0,
      total,
      scarce: false,
    });
    return;
  }
  // one level of nesting: the scarce-token bag
  if (!value || typeof value !== "object") return;
  const nestedR = (reserved[key] ?? {}) as Record<string, unknown>;
  for (const [k2, v2] of Object.entries(value as Record<string, unknown>)) {
    const t2 = num(v2);
    if (t2 == null) continue;
    out.push({
      key: `${key}.${k2}`,
      label: pretty(k2),
      used: num(nestedR[k2]) ?? 0,
      total: t2,
      scarce: true,
    });
  }
}

export function capacityRows(capacity: unknown): CapacityRow[] {
  if (!capacity || typeof capacity !== "object") return [];
  const cap = capacity as Record<string, unknown>;
  const budget = (cap.budget ?? {}) as Record<string, unknown>;
  const reserved = (cap.reserved ?? {}) as Record<string, unknown>;
  const out: CapacityRow[] = [];
  for (const [k, v] of Object.entries(budget)) pushBudget(out, k, v, reserved);
  return out.sort(compareCapacity);
}

/*
 *
 * Prereq job ids, whatever shape they arrive in. BE-Light's per-field read documents `prereq_ids` as a
 * string, but the ADMIN read's documented projection doesn't list them at all — so this accepts the
 * array, a comma/space-separated string, and the `prereqs` spelling rather than rendering nothing and
 * leaving the user to conclude a queued job has no prerequisites when it does.
 */
export function prereqIdsOf(job: Record<string, unknown>): string[] {
  const raw = job.prereq_ids ?? job.prereqs ?? job.prereq_job_ids;
  if (Array.isArray(raw))
    return raw.filter((x): x is string => typeof x === "string" && !!x);
  if (typeof raw === "string" && raw.trim())
    return raw.split(/[,\s]+/).filter(Boolean);
  return [];
}

const RANK: Record<string, number> = { in_progress: 0, waiting: 1, blocked: 2 };

type DisplayJob = {
  status?: string | null;
  queue_position?: number | null;
  completed_at?: string | null;
  created_at?: string | null;
  job_id?: string;
};

function finishedAt(job: DisplayJob): string {
  return job.completed_at ?? job.created_at ?? "";
}

function compareTerminal(a: DisplayJob, b: DisplayJob): number {
  // newest completion first; anything undated sinks rather than jumping the list
  return (
    finishedAt(b).localeCompare(finishedAt(a)) ||
    (a.job_id ?? "").localeCompare(b.job_id ?? "")
  );
}

function statusRank(status: string | null | undefined): number {
  return RANK[status ?? ""] ?? 3;
}

function queueOrder(position: number | null | undefined): number {
  return position ?? Number.MAX_SAFE_INTEGER;
}

function compareActive(a: DisplayJob, b: DisplayJob): number {
  const byStatus = statusRank(a.status) - statusRank(b.status);
  if (byStatus !== 0) return byStatus;
  // within the queue, the position IS the order; unpositioned rows sort after positioned ones
  const byQueue = queueOrder(a.queue_position) - queueOrder(b.queue_position);
  if (byQueue !== 0) return byQueue;
  return (
    (a.created_at ?? "").localeCompare(b.created_at ?? "") ||
    (a.job_id ?? "").localeCompare(b.job_id ?? "")
  );
}

/*
 *
 * TERMINAL lists read as history — newest first, because the job you're looking for after a failure
 * is almost always the most recent one.
 */
export function sortJobsForDisplay<T extends DisplayJob>(
  jobs: T[],
  view: "active" | "terminal",
): T[] {
  const out = [...jobs];
  out.sort(view === "terminal" ? compareTerminal : compareActive);
  return out;
}
